import type { ApptorChatAPI, BootConfig, LoaderEvent } from './types';

export interface InternalAPI extends ApptorChatAPI {
  __bootedConfig?: BootConfig;
  __pendingBootMerge: Partial<BootConfig>;
  __listeners: Map<LoaderEvent, Set<(...args: unknown[]) => void>>;
  __cmdQueue: Array<{ cmd: string; args: unknown[] }>;
}

export function createApi(): InternalAPI {
  const listeners = new Map<LoaderEvent, Set<(...args: unknown[]) => void>>();
  const queue: InternalAPI['__cmdQueue'] = [];
  const pendingMerge: Partial<BootConfig> = {};

  const api: InternalAPI = {
    __pendingBootMerge: pendingMerge,
    __listeners: listeners,
    __cmdQueue: queue,

    boot(config) {
      const merged = { ...config, ...pendingMerge };
      api.__bootedConfig = merged;
      queue.push({ cmd: 'boot', args: [merged] });
    },

    update(partial) {
      queue.push({ cmd: 'update', args: [partial] });
    },

    open() { queue.push({ cmd: 'open', args: [] }); },
    close() { queue.push({ cmd: 'close', args: [] }); },
    toggle() { queue.push({ cmd: 'toggle', args: [] }); },

    reset() { queue.push({ cmd: 'reset', args: [] }); },

    identify(user) {
      (pendingMerge as any).user = user;
      queue.push({ cmd: 'identify', args: [user] });
    },

    setContext(ctx) {
      (pendingMerge as any).context = ctx;
      queue.push({ cmd: 'setContext', args: [ctx] });
    },

    on(event, cb) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(cb);
      return () => listeners.get(event)?.delete(cb);
    },

    destroy() {
      queue.push({ cmd: 'destroy', args: [] });
      listeners.clear();
    }
  };

  return api;
}

export function emit(api: InternalAPI, event: LoaderEvent, payload?: unknown): void {
  api.__listeners.get(event)?.forEach(cb => {
    try { cb(payload); } catch (e) { console.warn('[ApptorChat] listener threw', e); }
  });
}
